import { Position } from '@turf/helpers'
import { footfallClient, mockData, tradeAreaClient } from 'src/clients'
import {
  TradeAreaType as FootTrafficType,
  TradeAreaDateRange,
  DriveTimeTypes,
  FootfallSectionDetails,
  CommonTradeAreaSection,
  ConsumerSpendRadiusData,
  NearVenue,
  SimpleVenueWithDistance,
  FootfallTradeAreaDetailsResponse,
  TrendsResponse,
  TrendsPeriod,
  CommonTradeAreaDetailsResponse,
  DriveTimeTradeAreaDetailsRequest,
  FootfallTradeAreaDetailsRequest,
  FootfallTradeAreaMapRequest,
  PromiseCache,
  DemoRadiiResponse,
} from 'src/common-services'
import { atom, selector, selectorFamily, waitForAll } from 'recoil'
import { DEFAULT_LRU_SIZE, keyFactory, retryAtom, uncachedSelector, uncachedSelectorFamily } from 'src/utils/recoil'
import { googlePlace, venueMatch } from 'src/atoms/search'
import { ifNotFound } from 'src/utils/ajax'
import { cellArea } from 'h3-js'
import { sqKmToMiles } from 'src/utils/convert'
import { selectedCategory } from 'src/components/insights/performance-benchmarks/atoms'
import { trendDatesByPeriod } from 'src/components/insights/common/visits/atoms'
import { browserLogger } from 'src/utils/browser-logger'

import { isPaidUser } from 'src/atoms/user'
import * as driveTimeAtoms from 'src/atoms/drive-time'
import * as radiusRingsAtoms from 'src/atoms/radius-rings'
import * as atomsCompare from 'src/atoms/compare'
import * as atomsColors from 'src/atoms/colors'
import { customTradeArea, customTradeAreaDetails, customTradeAreaMapOverview } from 'src/atoms/custom-tta'
import { computeHispanicPercentage } from 'src/atoms/demographics'

export * from './trends-chart'

const FOOT_TRAFFIC_PERCENTAGES_UNIFIED = [100]
export const ALL_DEMOGRAPHICS_CATEGORIES = [
  'Overview',
  'Households',
  'Housing Units',
  'Gender',
  'Ethnicity',
  'Age',
  'Marital Status',
  'Value of Owner Occupied Housing Units',
  'Types of Housing Units',
  'Household Income',
  'Education',
  'Labor Force',
  'Transportation to Work',
  'Travel Time to Work',
  'Population by Industry',
  'Population by Occupation',
]

// Cached API Requests
const cacheGetTradeAreaDateRanges = new PromiseCache()
const cacheGetFootfallTradeAreaDetails = new PromiseCache()
const cacheGetFootfallNearby = new PromiseCache()
const cacheGetDriveTimeNearby = new PromiseCache()
const cacheGetFootfallTradeAreaMap = new PromiseCache()

const key = keyFactory('insights-trade-area-map')

function assertNever(_value: never): never {
  throw new Error('Value should never exist')
}

export enum TradeAreaType {
  FOOT_TRAFFIC,
  DRIVE_TIME,
  RADIUS_RINGS,
  CUSTOM_POLYGON,
}

export const type = atom<TradeAreaType>({
  key: key('type'),
  default: TradeAreaType.RADIUS_RINGS,
})

export const footTrafficOverlayRadius = atom<boolean>({
  key: key('foot-traffic-overlay-radius'),
  default: false,
})

export const footTrafficTypeHome = atom<boolean>({
  key: key('foot-traffic-type-home'),
  default: true,
})

export const footTrafficTypeWork = atom<boolean>({
  key: key('foot-traffic-type-work'),
  default: false,
})

export const footTrafficType = selector<FootTrafficType>({
  key: key('foot-traffic-type'),
  get: ({ get }) => {
    const footTrafficTypeHomeValue = get(footTrafficTypeHome)
    const footTrafficTypeWorkValue = get(footTrafficTypeWork)
    if (footTrafficTypeHomeValue && footTrafficTypeWorkValue) {
      return FootTrafficType.BOTH
    }
    if (footTrafficTypeWorkValue) {
      return FootTrafficType.WORK
    }
    return FootTrafficType.HOME
  },
})

export const footTrafficPercentages = atom<number[]>({
  key: key('foot-traffic-percentages'),
  default: [70, 20, 10],
})

export const driveTimeOverlayRadius = atom<boolean>({
  key: key('drive-time-overlay-radius'),
  default: false,
})

export const driveTimeOverlayRadiusRings = atom<number[]>({
  key: key('drive-time-overlay-radius-rings'),
  default: [1, 3, 5],
})

export const driveTimeType = atom<DriveTimeTypes>({
  key: key('drive-time-type'),
  default: DriveTimeTypes.ARRIVAL,
})

export const driveTimeHour = atom<number>({
  key: key('drive-time-hour'),
  default: 6,
})

export const driveTimeDay = atom<number>({
  key: key('drive-time-day'),
  default: 0,
})

export const driveTimeDurations = atom<number[]>({
  key: key('drive-time-durations'),
  default: [5, 10, 15],
})

export const columnLabels = selector<string[]>({
  key: key('column-labels'),
  get: ({ get }) => {
    const viewType = get(type)
    if (viewType === TradeAreaType.FOOT_TRAFFIC) {
      return ['Primary Foot Traffic', 'Secondary Foot Traffic', 'Tertiary Foot Traffic']
    }
    if (viewType === TradeAreaType.DRIVE_TIME) {
      const durations = get(driveTimeAtoms.driveTimeDurations)
      return durations.map((dur) => `${dur} minutes`)
    } else {
      const rads = get(radiusRingsAtoms.radii)
      return rads.map((rad) => `${rad} miles`)
    }
  },
})

export const venue = uncachedSelector({
  key: key('venue'),
  get: async ({ get }) => {
    return get(venueMatch)
  },
  lru: DEFAULT_LRU_SIZE,
})

export const dates = selector<TradeAreaDateRange[]>({
  key: key('dates'),
  get: ({ get }) => {
    get(retryAtom)
    return ifNotFound(
      cacheGetTradeAreaDateRanges.get({}, () => tradeAreaClient.getTradeAreaDateRanges()),
      []
    )
  },
})

export const selectedDate = atom<number>({
  key: key('selected-date'),
  default: 0,
})

const extraFootfallMapRequest = selectorFamily<FootfallTradeAreaMapRequest[], boolean>({
  key: key('extra-footfall-map-request'),
  get:
    (compare: boolean) =>
    ({ get }) => {
      get(retryAtom)
      const places = get(atomsCompare.googlePlaces)
      const venues = get(atomsCompare.venues)
      const footTrafficTypeValue = get(footTrafficType)
      const datesValue = get(dates)
      const selectedDateValue = get(selectedDate)
      const percentages = compare ? FOOT_TRAFFIC_PERCENTAGES_UNIFIED : get(footTrafficPercentages)

      if (!datesValue[selectedDateValue]) {
        return []
      }
      if (!venues.length) {
        return []
      }

      return places.reduce((out: FootfallTradeAreaMapRequest[], place, index) => {
        if (!venues[index] || !place) {
          browserLogger.error('no venue or place for trade area - this should never happen')
          return out
        }
        out.push({
          venueId: venues[index]?.venueId || '',
          dateRange: datesValue[selectedDateValue],
          type: footTrafficTypeValue,
          center: place.geometry.location,
          percentages: percentages,
        })
        return out
      }, [])
    },
})

const footfallMapRequest = selectorFamily<FootfallTradeAreaMapRequest | undefined, boolean>({
  key: key('footfall-map-request'),
  get:
    (compare: boolean) =>
    ({ get }) => {
      get(retryAtom)
      const place = get(googlePlace)
      const venue = get(venueMatch)
      if (!venue || !place) {
        browserLogger.error('no venue or place for trade area - this should never happen')
        return undefined
      }
      const { venueId } = venue
      const footTrafficTypeValue = get(footTrafficType)
      const datesValue = get(dates)
      const selectedDateValue = get(selectedDate)

      if (!datesValue[selectedDateValue]) {
        return undefined
      }

      const percentages = compare ? FOOT_TRAFFIC_PERCENTAGES_UNIFIED : get(footTrafficPercentages)

      const req = {
        venueId,
        dateRange: datesValue[selectedDateValue],
        type: footTrafficTypeValue,
        center: place.geometry.location,
        percentages: percentages,
      }
      return req
    },
})

const footfallRequest = selectorFamily<FootfallTradeAreaDetailsRequest | undefined, boolean>({
  key: key('footfall-request'),
  get:
    (compare: boolean) =>
    ({ get }) => {
      const mapRequest = get(footfallMapRequest(compare))
      if (mapRequest == null) {
        return undefined
      }
      return {
        ...mapRequest,
        naicsCode: get(selectedCategory),
      }
    },
})

const extraFootfallRequest = selectorFamily<FootfallTradeAreaDetailsRequest[], boolean>({
  key: key('extra-footfall-request'),
  get:
    (compare: boolean) =>
    ({ get }) => {
      const mapRequests = get(extraFootfallMapRequest(compare))
      return mapRequests.map((req) => ({
        ...req,
        naicsCode: get(selectedCategory),
      }))
    },
})

const getFootfallTradeAreaDetails = selectorFamily<
  FootfallTradeAreaDetailsResponse | undefined,
  FootfallTradeAreaDetailsRequest
>({
  key: key('get-trade-area-by-footfall'),
  get: (req: FootfallTradeAreaDetailsRequest) => async () => {
    return await ifNotFound(
      cacheGetFootfallTradeAreaDetails.get(req, (args) => tradeAreaClient.getFootfallTradeAreaDetails(args)),
      undefined
    )
  },
})

const currentAreaByFootFall = selectorFamily<FootfallTradeAreaDetailsResponse | undefined, boolean>({
  key: key('current-trade-area-by-footfall'),
  get:
    (compare: boolean) =>
    async ({ get }) => {
      const baseReq = get(footfallRequest(compare))
      if (!baseReq) {
        return undefined
      }
      return get(getFootfallTradeAreaDetails(baseReq))
    },
})

const extraAreaByFootFall = selectorFamily<Array<FootfallTradeAreaDetailsResponse | undefined>, boolean>({
  key: key('extra-trade-area-by-footfall'),
  get:
    (compare: boolean) =>
    async ({ get }) => {
      const extraReq = get(extraFootfallRequest(compare))
      const response = get(waitForAll(extraReq.map((req) => getFootfallTradeAreaDetails(req))))
      return response
    },
})

const tradeAreaByFootFall = selectorFamily<Array<FootfallTradeAreaDetailsResponse | undefined>, boolean>({
  key: key('trade-area-by-footfall'),
  get:
    (compare: boolean) =>
    async ({ get }) => {
      if (!get(isPaidUser)) {
        return [await mockData.get('trade-area-footfall-details')]
      }
      const base = get(currentAreaByFootFall(compare))
      const extra = get(extraAreaByFootFall(compare))
      return [base, ...extra]
    },
})

export const ttaCenterPoint = atom<Position | undefined>({
  key: key('tta-center-point'),
  default: undefined,
})

export const ttaPosition = selector<Position | undefined>({
  key: key('tta-position'),
  get: ({ get }) => {
    const centerPoint = get(ttaCenterPoint)
    if (centerPoint) {
      return centerPoint
    }
    const place = get(googlePlace)
    return place?.geometry.location
  },
})

export const hasDetails = selectorFamily<boolean, boolean>({
  key: key('has-details'),
  get:
    (compare: boolean) =>
    ({ get }) => {
      const viewType = get(type)
      if (viewType === TradeAreaType.RADIUS_RINGS) {
        const position = get(ttaPosition)
        return get(radiusRingsAtoms.hasDetails(position)) != null
      }
      if (viewType === TradeAreaType.FOOT_TRAFFIC) {
        return get(tradeAreaByFootFall(compare)) != null
      }
      if (viewType === TradeAreaType.CUSTOM_POLYGON) {
        return get(customTradeArea) !== undefined
      } else {
        return get(driveTimeAtoms.hasDetails)
      }
    },
})

export const mapLoaded = selectorFamily<boolean, boolean>({
  key: key('map-loaded'),
  get:
    (compare: boolean) =>
    ({ get }) => {
      const viewType = get(type)
      if (viewType === TradeAreaType.RADIUS_RINGS) {
        return true
      }
      if (viewType === TradeAreaType.FOOT_TRAFFIC) {
        return get(footfallMap(compare)) != null
      } else {
        return get(driveTimeAtoms.mapPolygons) != null
      }
    },
})

const nearby = uncachedSelector<CommonTradeAreaDetailsResponse | undefined>({
  key: key('common-details'),
  get: ({ get }) => {
    const viewType = get(type)
    if (viewType === TradeAreaType.FOOT_TRAFFIC) {
      const req = get(footfallRequest(false))
      if (!req) {
        return undefined
      }
      if (!get(isPaidUser)) {
        return mockData.get('trade-area-footfall-nearby')
      }
      return ifNotFound(
        cacheGetFootfallNearby.get(req, (args) => tradeAreaClient.getFootfallNearby(args)),
        undefined
      )
    }
    if (viewType === TradeAreaType.DRIVE_TIME) {
      const req = get(driveTimeAtoms.request)
      if (!req) {
        return undefined
      }
      return ifNotFound(
        cacheGetDriveTimeNearby.get(req as DriveTimeTradeAreaDetailsRequest, (args) =>
          tradeAreaClient.getDriveTimeNearby(args)
        ),
        undefined
      )
    }
    if (viewType === TradeAreaType.RADIUS_RINGS) {
      const position = get(ttaPosition)
      return get(radiusRingsAtoms.nearby(position))
    }
    if (viewType === TradeAreaType.CUSTOM_POLYGON) {
      return undefined
    }
    assertNever(viewType)
  },
  lru: DEFAULT_LRU_SIZE,
})

export const getFootfallTradeAreaMap = uncachedSelectorFamily<FootfallSectionDetails[], FootfallTradeAreaMapRequest>({
  key: key('get-trade-area-breakdown'),
  get: (req: FootfallTradeAreaMapRequest) => async () => {
    return await ifNotFound(
      cacheGetFootfallTradeAreaMap.get(req, (args) => tradeAreaClient.getFootfallTradeAreaMap(args)),
      []
    )
  },
  lru: DEFAULT_LRU_SIZE,
})

export const currentFootfallMap = uncachedSelectorFamily<FootfallSectionDetails[], boolean>({
  key: key('current-trade-area-breakdown'),
  get:
    (compare: boolean) =>
    async ({ get }) => {
      const baseReq = get(footfallMapRequest(compare))
      if (!baseReq) {
        return []
      }
      return get(getFootfallTradeAreaMap(baseReq))
    },
  lru: DEFAULT_LRU_SIZE,
})

export const extraFootfallMap = uncachedSelectorFamily<FootfallSectionDetails[][], boolean>({
  key: key('extra-trade-area-breakdown'),
  get:
    (compare: boolean) =>
    async ({ get }) => {
      const extraReq = get(extraFootfallMapRequest(compare))
      const response = get(waitForAll(extraReq.map((req) => getFootfallTradeAreaMap(req))))
      return response
    },
  lru: DEFAULT_LRU_SIZE,
})

export const footfallMap = uncachedSelectorFamily<FootfallSectionDetails[][], boolean>({
  key: key('trade-area-breakdown'),
  get:
    (compare: boolean) =>
    async ({ get }) => {
      if (!get(isPaidUser)) {
        return [await mockData.get('trade-area-footfall-map')]
      }
      const base = get(currentFootfallMap(compare))
      const extra = get(extraFootfallMap(compare))
      if (!base) {
        return []
      }
      return [base, ...extra]
    },
  lru: DEFAULT_LRU_SIZE,
})

const tradeAreaSections = uncachedSelectorFamily<CommonTradeAreaSection[], boolean>({
  key: key('common-sections'),
  get:
    (compare: boolean) =>
    async ({ get }) => {
      const viewType = get(type)
      if (viewType === TradeAreaType.FOOT_TRAFFIC) {
        return get(tradeAreaByFootFall(compare)).flatMap((place) => place?.sections ?? [])
      }
      if (viewType === TradeAreaType.DRIVE_TIME) {
        return get(driveTimeAtoms.tradeArea)?.sections ?? []
      }
      if (viewType === TradeAreaType.RADIUS_RINGS) {
        const position = get(ttaPosition)
        return get(radiusRingsAtoms.tradeArea(position))?.sections ?? []
      }
      if (viewType === TradeAreaType.CUSTOM_POLYGON) {
        return get(customTradeAreaDetails)?.sections || []
      }
      assertNever(viewType)
    },
  lru: DEFAULT_LRU_SIZE,
})

const breakpoints = [0.5, 0.75, 1, 2, 3, 5, 7, 10, 13, 15, 17, 20]

type DistanceBucket = {
  distance: number
  visitCount: number
  visitPercentage: number
}
// distance breakdown for chart of visit percentile by distance
export const tradeAreaDistanceBreakdown = uncachedSelectorFamily<DistanceBucket[][], boolean>({
  key: key('distance-breakdown'),
  get:
    (compare: boolean) =>
    ({ get }) => {
      const breakdowns = get(footfallMap(compare))
      return breakdowns.map((breakdown) => {
        const totalVisits = breakdown.flatMap((x) => x.items).reduce((acc, item) => item.count + acc, 0)
        const buckets: DistanceBucket[] = breakpoints.map((val) => ({
          distance: val,
          visitCount: 0,
          visitPercentage: 0,
        }))
        const items = breakdown.flatMap((x) => x.items)
        for (let i = 0; i < buckets.length; i++) {
          const nextBucket = buckets[i + 1]
          const bucket = buckets[i]
          const isLastBucket = !nextBucket
          const isFirstBucket = i === 0
          if (isFirstBucket) {
            bucket.visitCount = items.reduce((acc, item) => {
              // item is in first bucket if its distance is less than or equal to the min bucket distance
              // e.g. 0.5 is in first bucket, 0.6 is not
              return item.distanceMiles <= bucket.distance ? item.count + acc : acc
            }, 0)
          } else if (isLastBucket) {
            bucket.visitCount = totalVisits
          } else {
            bucket.visitCount = items.reduce((acc, item) => {
              // item is in intermediate bucket if it is greater than this bucket but less than the next bucket
              // e.g. 0.6 is not in first bucket but is in second bucket and is not in 3rd bucket
              const isInBucket = item.distanceMiles <= bucket.distance && item.distanceMiles < nextBucket.distance
              return isInBucket ? item.count + acc : acc
            }, 0)
          }
        }

        for (const bucket of buckets) {
          bucket.visitPercentage = bucket.visitCount / totalVisits
        }

        const smallestFullBucketIndex = buckets.findIndex((x) => x.visitPercentage === 1)
        // return fewer than 20 (but no less than 10) buckets if there are a few buckets at the end which all have 100%
        return smallestFullBucketIndex < breakpoints.length
          ? buckets.slice(0, Math.max(smallestFullBucketIndex, 10))
          : buckets
      })
    },
  lru: DEFAULT_LRU_SIZE,
})

export const demographicsExpandedCategories = atom<string[]>({
  key: key('demographics-expanded-categories'),
  default: ['Overview', 'Households'],
})

export const venuesInTradeArea = uncachedSelector<NearVenue[]>({
  key: key('nearby-venues'),
  get: ({ get }) => {
    const match = get(venueMatch)
    // this will never be the case here
    if (!match) {
      return []
    }
    const data = get(nearby)
    const venues = data?.nearbyVenues ?? []
    return venues.filter((x) => x.venueId !== match.venueId)
  },
  lru: DEFAULT_LRU_SIZE,
})

export const locationsInTradeArea = uncachedSelector<SimpleVenueWithDistance[]>({
  key: key('locations-in-trade-area'),
  get: ({ get }) => {
    const data = get(nearby)
    return data?.nearbyLocations ?? []
  },
  lru: DEFAULT_LRU_SIZE,
})

export const nearbyLocationCounts = uncachedSelectorFamily<Array<number | undefined>, boolean>({
  key: key('nearby-simple-venues'),
  get:
    (compare: boolean) =>
    ({ get }) => {
      const data = get(tradeAreaSections(compare))
      return data.map((x) => x.nearbyLocationCount)
    },
  lru: DEFAULT_LRU_SIZE,
})

type DemoOverview = {
  currentYear: number | undefined
  fiveYear: number | undefined
}

type View = {
  format: string
  name: string
  values: DemoOverview[]
}

type DemoOverviewRange = View[]

export const demoOverview = uncachedSelectorFamily<DemoOverviewRange | undefined, boolean>({
  key: key('demo-overview'),
  get:
    (compare: boolean) =>
    ({ get }) => {
      const viewType = get(type)
      switch (viewType) {
        case TradeAreaType.DRIVE_TIME:
          return get(driveTimeAtoms.demoOverview(compare))
        case TradeAreaType.RADIUS_RINGS:
          const position = get(ttaPosition)
          return get(radiusRingsAtoms.demoOverview({ position, compare }))
      }
    },
  lru: DEFAULT_LRU_SIZE,
})

export const consumerSpendData = uncachedSelectorFamily<ConsumerSpendRadiusData[] | undefined, boolean>({
  key: key('spend-data'),
  get:
    (compare: boolean) =>
    async ({ get }) => {
      const tradeAreas = get(tradeAreaSections(compare))
      return tradeAreas.map((area) => {
        return area.consumerSpend
      })
    },
  lru: DEFAULT_LRU_SIZE,
})

const areaMiles = (block: FootfallSectionDetails): number | null => {
  if (!block?.items?.length) {
    return null
  }
  const areaKmSq = cellArea(block.items[0].h3, 'km2') * block.items.length
  return sqKmToMiles(areaKmSq)
}

export const sizeMiles = selectorFamily<Array<number | null>, boolean>({
  key: key('size-miles'),
  get:
    (compare: boolean) =>
    ({ get }) => {
      const viewType = get(type)
      if (viewType === TradeAreaType.FOOT_TRAFFIC) {
        const things = get(footfallMap(compare))
        return things.flatMap((x) => x.map(areaMiles))
      }
      if (viewType === TradeAreaType.RADIUS_RINGS) {
        return get(radiusRingsAtoms.sizeMiles)
      }
      if (viewType === TradeAreaType.DRIVE_TIME) {
        return get(driveTimeAtoms.sizeMiles)
      }
      if (viewType === TradeAreaType.CUSTOM_POLYGON) {
        const overview = get(customTradeAreaMapOverview)
        return overview.flat().map(areaMiles)
      }
      assertNever(viewType)
    },
})

export const demographicsCategories = selector<string[]>({
  key: key('demographics-categories'),
  get: ({ get }) => {
    const viewType = get(type)
    switch (viewType) {
      case TradeAreaType.RADIUS_RINGS:
        const position = get(ttaPosition)
        return get(radiusRingsAtoms.demographicsCategories({ position, compare: true }))
      case TradeAreaType.DRIVE_TIME:
      default:
        return get(driveTimeAtoms.demographicsCategories)
    }
  },
})

export const demographicsData = uncachedSelector<DemoRadiiResponse | undefined>({
  key: key('demographics-data'),
  get: async ({ get }) => {
    const tradeAreas = get(customTradeAreaDetails)?.sections ?? []
    return tradeAreas.map((area, i) => {
      const data = computeHispanicPercentage(area.demographics)
      return {
        data,
        radius: 0,
      }
    })
  },
  lru: DEFAULT_LRU_SIZE,
})

const monthTrendsForVenue = uncachedSelectorFamily<TrendsResponse | undefined, string>({
  key: key('trend-for-venue'),
  get:
    (venueId) =>
    ({ get }) => {
      const months = get(trendsChartMonths)
      const start = months[0]
      const end = months[months.length - 1]
      if (!start || !end) {
        return undefined
      }
      return ifNotFound(
        footfallClient.getTrends({
          start,
          end,
          period: TrendsPeriod.MONTHLY,
          venueId,
        }),
        undefined
      )
    },
  lru: DEFAULT_LRU_SIZE,
})

export const trendsChartMonths = selector<string[]>({
  key: key('trends-chart-categories'),
  get: ({ get }) => {
    const dates = get(trendDatesByPeriod(TrendsPeriod.MONTHLY)).periods
    const months = dates.slice(-12)
    if (months.length < 3) {
      return []
    }
    return months
  },
})

export type TrendsChartSeriesData = {
  name: string
  data: number[]
  color?: string
}

export const trendsChartData = selector<TrendsChartSeriesData[]>({
  key: key('trends-chart-data'),
  get: ({ get }) => {
    const venue = get(venueMatch)
    if (!venue) {
      return []
    }
    const venues = get(venuesInTradeArea)
    const nearby = venues.slice(0, 4)
    const ids = [venue.venueId].concat(nearby.map((x) => x.venueId))
    const venueTrends = ids.map((id) => get(monthTrendsForVenue(id)))
    const results: TrendsChartSeriesData[] = []
    for (let i = 0; i < venueTrends.length; i++) {
      const res = venueTrends[i]
      if (res == null) {
        continue
      }
      const name = i === 0 ? venue.brand ?? 'venue' : venues[i - 1].brand

      results.push({
        name,
        data: res.map((x) => x.personCountSum),
      })
    }
    return results
  },
})

export const trendsChartDataCompare = selector<TrendsChartSeriesData[]>({
  key: key('trends-chart-data-compare'),
  get: ({ get }) => {
    const colors = get(atomsColors.gradientsTopColors)
    const venue = get(venueMatch)
    if (!venue) {
      return []
    }
    const extraVenues = get(atomsCompare.venues)
    const extraVenuesIds: string[] = extraVenues.flatMap((x) => (x ? [x.venueId] : []))
    const ids = [venue.venueId, ...extraVenuesIds]
    const venueTrends = ids.map((id) => get(monthTrendsForVenue(id)))
    const results: TrendsChartSeriesData[] = []
    for (let i = 0; i < venueTrends.length; i++) {
      const res = venueTrends[i]
      if (res == null) {
        continue
      }
      const name = i === 0 ? venue.brand ?? 'venue' : extraVenues[i - 1]?.brand || '-'
      results.push({
        name,
        data: res.map((x) => x.personCountSum),
        color: colors[i],
      })
    }
    return results
  },
})

export const showTradeAreaSize = atom<boolean>({
  key: key('show-trade-area-size'),
  default: true,
})

export const showSameCategory = atom<boolean>({
  key: key('show-same-category'),
  default: true,
})

export const showDemographicOverview = atom<boolean>({
  key: key('show-demographic-Overview'),
  default: true,
})

export const showCoverageByDistance = atom<boolean>({
  key: key('show-coverage-by-distance'),
  default: true,
})

export const showTradeAreaOverlap = atom<boolean>({
  key: key('show-trade-area-overlap'),
  default: true,
})

export const demographicsDisplayCategories = atom<string[]>({
  key: key('demographics-display-categories'),
  default: ALL_DEMOGRAPHICS_CATEGORIES,
})

export const showConsumerSpend = atom<boolean>({
  key: key('show-consumer-spend'),
  default: true,
})

export const showWithinComparison = atom<boolean>({
  key: key('show-within-comparison'),
  default: true,
})

export const showNearestCategory = atom<boolean>({
  key: key('show-nearest-category'),
  default: true,
})

export const showTrendComparisonChart = atom<boolean>({
  key: key('show-trend-comparison-chart'),
  default: true,
})
